/**
 * 网络配置
 * 集中管理所有网络请求相关的配置、拦截器和工具
 */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';

// ===== 开发配置 =====

// ===== 环境检测 =====

// 确定当前环境
const env = process.env.NODE_ENV || 'development';

// ===== 基础配置 =====
// 所有环境都使用的基础配置
const baseConfig = {
  BASE_URL: '',              // 由环境特定配置提供
  API_PREFIX: '/api',        // 开发和生产都有/api前缀
  TIMEOUT: 15000,
  RETRY_COUNT: 3,
  RETRY_DELAY: 2000,
  USE_MOCK_DATA: false,      // 默认不使用Mock
  DEBUG: false               // 默认不开启调试
};

// ===== 环境特定配置 =====

// 开发环境配置
const developmentConfig = {
  BASE_URL: process.env.REACT_APP_API_BASE_URL || (() => {
    console.warn('⚠️ REACT_APP_API_BASE_URL 未配置，请设置环境变量');
    return 'http://localhost:50035'; // 仅开发环境fallback
  })(),
  USE_MOCK_DATA: false,                // 开发时使用真实API
  DEBUG: true,                         // 开启调试模式
  LOG_LEVEL: 'debug'
};

// 生产环境配置
const productionConfig = {
  BASE_URL: (() => {
    const url = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || 'http://space.homaxs.com';
    if (process.env.NODE_ENV === 'production' && (!url || url.includes('localhost'))) {
      throw new Error('🚨 生产环境必须配置有效的域名，不能使用 localhost');
    }
    return url;
  })(),
  USE_MOCK_DATA: false,                // 生产环境使用真实API
  DEBUG: false,                        // 关闭调试模式
  LOG_LEVEL: 'warn'
};

// ===== 配置合并逻辑 =====

// 创建最终配置
const createConfig = () => {
  // 1. 从基础配置开始
  let config = { ...baseConfig };

  // 2. 根据环境合并特定配置
  if (env === 'development') {
    config = { ...config, ...developmentConfig };
    console.log('🔧 使用开发环境配置:', config);

    // 开发环境允许环境变量覆盖
    config.BASE_URL = process.env.REACT_APP_API_URL || config.BASE_URL;
    console.log('🔧 开发环境API地址:', config.BASE_URL);

    return config;
  } else if (env === 'production') {
    config = { ...config, ...productionConfig };
    console.log('🚀 使用生产环境配置:', config);

    // 生产环境才允许环境变量覆盖
    config = {
      ...config,
      BASE_URL: process.env.REACT_APP_API_URL || config.BASE_URL,
      USE_MOCK_DATA: process.env.REACT_APP_USE_MOCK_DATA === 'true' || config.USE_MOCK_DATA,
      DEBUG: process.env.REACT_APP_DEBUG === 'true' || config.DEBUG
    };
  }

  console.log('🎯 最终配置:', config);
  return config;
};

// 生成最终配置
const finalConfig = createConfig();

// 导出配置
export const API_CONFIG = {
  BASE_URL: finalConfig.BASE_URL,
  API_PREFIX: finalConfig.API_PREFIX,
  TIMEOUT: finalConfig.TIMEOUT,
  RETRY_COUNT: finalConfig.RETRY_COUNT,
  RETRY_DELAY: finalConfig.RETRY_DELAY

};

// 导出模拟数据配置
export const USE_MOCK_DATA = finalConfig.USE_MOCK_DATA;

// 其他网络相关的通用配置
export const NET_CONFIG = {
  // 分页配置
  PAGINATION: {
    DEFAULT_PAGE_SIZE: 10,
    DEFAULT_CURRENT_PAGE: 1
  },

  // 缓存配置
  CACHE: {
    ENABLED: true,
    TTL: 60000, // 缓存有效期，毫秒
    STORAGE_KEY_PREFIX: 'api_cache_'
  },

  // 调试选项
  DEBUG: {
    ENABLED: false, // 关闭调试日志
    LOG_REQUESTS: false,
    LOG_RESPONSES: false
  }
};

// ===== Axios实例配置 =====

// 请求计数器与加载状态
let requestCount = 0;
const loadingDelay = 300;
let loadingTimer: ReturnType<typeof setTimeout> | null = null;

// 创建axios实例
export const createApiClient = (): AxiosInstance => {
  // 合并baseURL和前缀
  const baseURL = `${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}`;

  const instance = axios.create({
    baseURL,
    timeout: API_CONFIG.TIMEOUT,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  // 请求拦截器
  instance.interceptors.request.use(
    (config) => {
      // 增加请求计数
      requestCount++;

      // 处理全局loading
      if (requestCount === 1 && !loadingTimer) {
        loadingTimer = setTimeout(() => {
          // 这里可以触发全局loading状态
          // 例如使用Redux: dispatch(setLoading(true))
        }, loadingDelay);
      }

      // 添加认证信息 - 统一使用 mslogin_token
      const token = localStorage.getItem('mslogin_token') ||
        sessionStorage.getItem('mslogin_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // 调试日志 - 已关闭
      // if (NET_CONFIG.DEBUG.ENABLED && NET_CONFIG.DEBUG.LOG_REQUESTS) {
      //   console.log(`🌐 请求: ${config.method?.toUpperCase()} ${config.url}`, config);
      // }

      return config;
    },
    (error) => {
      handleRequestComplete();
      return Promise.reject(error);
    }
  );

  // 响应拦截器
  instance.interceptors.response.use(
    (response) => {
      handleRequestComplete();

      // 调试日志 - 已关闭
      // if (NET_CONFIG.DEBUG.ENABLED && NET_CONFIG.DEBUG.LOG_RESPONSES) {
      //   console.log(`✅ 响应: ${response.config.url}`, response);
      // }

      return response;
    },
    async (error: AxiosError) => {
      handleRequestComplete();

      // 调试日志 - 已关闭
      // if (NET_CONFIG.DEBUG.ENABLED) {
      //   console.error(`❌ 错误: ${error.config?.url}`, error);
      // }

      // 处理重试逻辑
      const config = error.config as AxiosRequestConfig & { _retryCount?: number };

      if (config) {
        config._retryCount = config._retryCount || 0;

        // 如果可以重试且未超出重试次数
        if (shouldRetry(error) && config._retryCount < API_CONFIG.RETRY_COUNT) {
          config._retryCount += 1;

          // 延迟重试 - 429错误使用更长的延迟
          const delay = error.response?.status === 429 ? API_CONFIG.RETRY_DELAY * 3 : API_CONFIG.RETRY_DELAY;
          await new Promise(resolve => setTimeout(resolve, delay));

          return instance(config);
        }
      }

      // 统一错误处理
      handleApiError(error);

      return Promise.reject(error);
    }
  );

  return instance;
};

// 判断是否应该重试请求
function shouldRetry(error: AxiosError): boolean {
  // 网络错误应该重试
  if (error.message && error.message.includes('Network Error')) {
    return true;
  }

  // 服务器错误(5xx)应该重试
  if (error.response && error.response.status >= 500 && error.response.status < 600) {
    return true;
  }

  // 请求超时应该重试
  if (error.code === 'ECONNABORTED') {
    return true;
  }

  // 429错误(Too Many Requests)应该重试，但需要更长的延迟
  if (error.response && error.response.status === 429) {
    return true;
  }

  return false;
}

// 处理请求完成
function handleRequestComplete() {
  requestCount--;

  if (requestCount <= 0) {
    requestCount = 0;
    if (loadingTimer) {
      clearTimeout(loadingTimer);
      loadingTimer = null;
    }
    // 关闭全局loading
    // 例如使用Redux: dispatch(setLoading(false))
  }
}

// 处理API错误
function handleApiError(error: AxiosError) {
  // 这里暂时只打印错误，因为netConfig在组件外部
  // 实际的错误消息会由各个服务组件来处理
  if (error.response) {
    const status = error.response.status;
    const errorData = error.response.data as any;

    switch (status) {
    case 401:
      console.error('请求未授权');
      break;

    case 403:
      console.error('您没有权限执行此操作');
      break;

    case 404:
      console.error('请求的资源不存在');
      break;

    case 422:
      // 表单验证错误
      if (errorData && errorData.errors) {
        const firstError = Object.values(errorData.errors)[0];
        console.error(Array.isArray(firstError) ? firstError[0] : String(firstError));
      } else {
        console.error('提交的数据无效');
      }
      break;

    case 429:
      console.error('请求过于频繁，请稍后再试');
      break;

    case 500:
      console.error('服务器内部错误，请稍后再试');
      break;

    default:
      console.error(
        errorData?.message ||
          `请求失败(${status})：${error.response.statusText || '未知错误'}`
      );
    }
  } else if (error.request) {
    // 请求发出但没有收到响应
    console.error('无法连接到服务器，请检查网络连接');
  } else {
    // 请求配置出错
    console.error(`请求配置错误: ${error.message}`);
  }
}

// ===== API实例与工具函数 =====

// 创建默认API实例
export const apiClient = createApiClient();

// API初始化函数
export function setupNetConfig() {
  console.log('🌐 网络配置初始化:', {
    环境: env,
    使用模拟数据: USE_MOCK_DATA,
    API配置: API_CONFIG,
    网络配置: NET_CONFIG,
    环境变量: {
      NODE_ENV: process.env.NODE_ENV,
      REACT_APP_API_URL: process.env.REACT_APP_API_URL,
      REACT_APP_USE_MOCK_DATA: process.env.REACT_APP_USE_MOCK_DATA,
      REACT_APP_DEBUG: process.env.REACT_APP_DEBUG
    }
  });

  // 可以在此添加其他初始化逻辑
}

export const api = createApiClient();

export default {
  setup: setupNetConfig,
  apiClient: api,
  apiConfig: API_CONFIG,
  config: NET_CONFIG,  // 改为 config 而不是 netConfig
  createApiClient,
  baseUrl: `${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}`
} as const;